import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import ActiveFilters, { ActiveFilterChip } from '../../../components/novarium/ActiveFilters'
import MoreFiltersToggle from '../../../components/novarium/MoreFiltersToggle'
import PeriodField from '../../../components/novarium/PeriodField'
import { VIRTUAL_PANEL_CLASS, VIRTUAL_SCROLL } from '../../../components/novarium/virtualOptions'
import { HospitalizationOptionsDTO } from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import {
  HOSPITALIZATION_MORE_FILTERS,
  HospitalizationFilterField,
  HospitalizationSelectFilters,
  NO_SELECT_FILTERS,
} from '../hospitalizationFilterModel'
import { HospitalizationFiltersData } from '../useHospitalizationFilters'

/** Valor de la opción «Todos»: el combo no admite `null` como valor seleccionado. */
const ALL = 'all'

const MORE_FILTERS_ID = 'hospitalization-more-filters'

/** Los combos de «Más filtros»; las cunas son un interruptor aparte. */
type ListField = 'sectorId' | 'admissionTypeId' | 'financierId'

interface SelectOption {
  value: string
  label: string
}

/** Un combo de «Más filtros»: sus opciones (valores de texto, `ALL` es «Todos») y cómo se lee y se escribe en los filtros. */
interface ListSpec {
  field: ListField
  labelId: string
  options: SelectOption[]
  /** Ancho del campo: S (228) o M (307, nombres largos). */
  wide?: boolean
  /** Lista larga: con buscador y scroll virtual. */
  long?: boolean
}

interface HospitalizationFiltersProps {
  data: HospitalizationFiltersData
  /** Opciones de los combos; `null` mientras no llegaron. */
  options: HospitalizationOptionsDTO | null
}

const asText = (value: number | null): string => (value === null ? ALL : String(value))

/**
 * Filtros de internación, todos al instante (no hay «Consultar»). A la vista, el «Período» (un selector con las fechas solo
 * en «Personalizado») y «Más filtros» (sector, tipo de ingreso, financiador y las cunas); debajo, los chips con lo aplicado
 * y «Quitar todos».
 */
export default function HospitalizationFilters({ data, options }: HospitalizationFiltersProps) {
  const intl = useIntl()
  const { filters, errors, period, rangeText, changeFilter, patchFilters, changeDate, commitDates, changePeriod } = data

  const optionsReady = options !== null
  const all = intl.formatMessage({ id: 'HospitalizationSummaryFilterAll' })

  const specs = useMemo<ListSpec[]>(() => {
    const byId = (list: { id: number; name: string }[] | undefined): SelectOption[] => [
      { value: ALL, label: all },
      ...(list ?? []).map((option) => ({ value: String(option.id), label: option.name })),
    ]
    return [
      { field: 'sectorId', labelId: 'HospitalizationSummaryFilterSector', options: byId(options?.sectors), wide: true },
      { field: 'admissionTypeId', labelId: 'HospitalizationSummaryFilterAdmissionType', options: byId(options?.admissionTypes), wide: true },
      { field: 'financierId', labelId: 'HospitalizationSummaryFilterFinancier', options: byId(options?.financiers), wide: true, long: true },
    ]
  }, [options, all])

  const dateErrors = useMemo(() => ({
    from: errors.from === undefined ? undefined : intl.formatMessage({ id: 'HospitalizationSummaryDateRequiredError' }),
    to: errors.to === undefined
      ? undefined
      : intl.formatMessage({ id: errors.to === 'order' ? 'HospitalizationSummaryDateOrderError' : 'HospitalizationSummaryDateRequiredError' }),
  }), [errors.from, errors.to, intl])

  const moreActive = HOSPITALIZATION_MORE_FILTERS.filter((field) => (field === 'includeNurseryBeds' ? filters[field] : filters[field] !== null)).length
  // «Más filtros» se abre solo cuando pasa a haber alguno aplicado (también si lo activa una fila del agrupado); después se puede cerrar
  const [moreOpen, setMoreOpen] = useState(moreActive > 0)
  useEffect(() => {
    if (moreActive > 0) setMoreOpen(true)
  }, [moreActive])

  /** Un chip por filtro elegido, «Filtro: valor»; las opciones todavía sin cargar se nombran por su valor. */
  const chips = useMemo<ActiveFilterChip<HospitalizationFilterField>[]>(() => {
    const list = specs.flatMap((spec) => {
      const value = asText(filters[spec.field])
      if (value === ALL) return []
      const name = spec.options.find((option) => option.value === value)?.label
        ?? intl.formatMessage({ id: 'HospitalizationSummaryChipUnknown' }, { id: value })
      return [{
        key: spec.field,
        label: intl.formatMessage({ id: 'HospitalizationSummaryChip' }, { filter: intl.formatMessage({ id: spec.labelId }), name }),
      }]
    })
    return filters.includeNurseryBeds
      ? [...list, { key: 'includeNurseryBeds' as const, label: intl.formatMessage({ id: 'HospitalizationSummaryFilterNursery' }) }]
      : list
  }, [specs, filters, intl])

  const onSelect = (field: ListField) => (event: DropdownChangeEvent) => {
    const value = event.value as string
    changeFilter({ [field]: value === ALL ? null : Number(value) } as Partial<HospitalizationSelectFilters>)
  }

  const removeChip = (field: HospitalizationFilterField) =>
    patchFilters(field === 'includeNurseryBeds' ? { includeNurseryBeds: false } : { [field]: null })

  return (
    <section className="nv-section" role="search" aria-label={intl.formatMessage({ id: 'HospitalizationSummaryFilters' })}>
      <div className="nv-form-row">
        <PeriodField
          idPrefix="hospitalization"
          period={period}
          rangeText={rangeText}
          from={filters.from}
          to={filters.to}
          errors={dateErrors}
          onDateChange={changeDate}
          onDatesCommit={commitDates}
          onPeriodChange={changePeriod}
        />
        <MoreFiltersToggle
          open={moreOpen}
          active={moreActive}
          controls={MORE_FILTERS_ID}
          onToggle={() => setMoreOpen((open) => !open)}
        />
      </div>

      {moreOpen && (
        <div id={MORE_FILTERS_ID} className="nv-form-row">
          {specs.map((spec) => {
            const inputId = `hospitalization-${spec.field}`
            return (
              <div key={spec.field} className={`nv-field ${spec.wide ? 'nv-w-m' : 'nv-w-s'}`}>
                <label htmlFor={inputId}>{intl.formatMessage({ id: spec.labelId })}</label>
                <Dropdown
                  inputId={inputId}
                  value={asText(filters[spec.field])}
                  options={spec.options}
                  optionLabel="label"
                  optionValue="value"
                  disabled={!optionsReady}
                  {...(spec.long
                    ? {
                      filter: true,
                      filterBy: 'label',
                      virtualScrollerOptions: VIRTUAL_SCROLL,
                      panelClassName: `nv-scope ${VIRTUAL_PANEL_CLASS}`,
                      emptyFilterMessage: intl.formatMessage({ id: 'HospitalizationSummaryFilterNoMatches' }),
                    }
                    : { panelClassName: 'nv-scope' })}
                  onChange={onSelect(spec.field)}
                />
              </div>
            )
          })}
          {/* Las cunas (Nursery) están fuera del indicador general: este control las suma (sin elegir, se ven solo como sector) */}
          <div className="nv-hosp-switch">
            <span className="nv-check-row">
              <InputSwitch
                inputId="hospitalization-includeNurseryBeds"
                checked={filters.includeNurseryBeds}
                onChange={(event) => changeFilter({ includeNurseryBeds: !!event.value })}
              />
              <label htmlFor="hospitalization-includeNurseryBeds">{intl.formatMessage({ id: 'HospitalizationSummaryFilterNursery' })}</label>
            </span>
          </div>
        </div>
      )}

      <ActiveFilters
        chips={chips}
        onRemove={removeChip}
        onClear={() => patchFilters(NO_SELECT_FILTERS)}
      />
    </section>
  )
}
